import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';
import { getCroppedImgBlob } from '../../utils/cropImage';
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react';
const ImageCropperModal = ({ isOpen, image, onCropComplete, onCancel }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const onCropChange = (crop) => setCrop(crop);

    const onCropAreaComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleCrop = async () => {
        try {
            const croppedImageBlob = await getCroppedImgBlob(image, croppedAreaPixels, rotation);
            onCropComplete(croppedImageBlob);
        } catch (e) {
            console.error(e);
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} onClose={onCancel} className="relative z-[9999]">
            {/* Le backdrop gère l'arrière-plan sombre */}
            <DialogBackdrop className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4">
                    <DialogPanel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl transition-all">
                        {/* ── Header ─────────────────────────────────────────── */}
                        {/*
                     * py shrinks from 3→2→1.5 as height classes tighten.
                     * We use [&]:h-* via inline style + a data attr trick if needed,
                     * but plain responsive padding covers most cases.
                     */}
                        <div className="
                        px-4 sm:px-6
                        py-2 sm:py-3
                        border-b border-gray-200 dark:border-gray-700
                        flex justify-between items-center
                        bg-gray-50 dark:bg-gray-800/50
                        shrink-0
                    ">
                            <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white leading-none">
                                Ajuster la photo
                            </h3>
                            <button
                                onClick={onCancel}
                                className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                                aria-label="Fermer"
                            >
                                <X className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* ── Cropper ────────────────────────────────────────── */}
                        {/*
                     * flex-1 lets it grow to fill whatever vertical space remains
                     * after the fixed-height header, sliders and footer.
                     * min-h uses a clamp so it never collapses to 0 on very short
                     * screens but also never over-claims space on tall ones.
                     */}
                        <div
                            className="relative bg-gray-900"
                            style={{ flex: '1 1 0', minHeight: 'clamp(140px, 38dvh, 480px)' }}
                        >
                            <Cropper
                                image={image}
                                crop={crop}
                                zoom={zoom}
                                rotation={rotation}
                                aspect={1}
                                cropShape="round"
                                showGrid={false}
                                onCropChange={onCropChange}
                                onCropComplete={onCropAreaComplete}
                                onZoomChange={setZoom}
                                onRotationChange={setRotation}
                            />
                        </div>

                        {/* ── Sliders ────────────────────────────────────────── */}
                        {/*
                     * On very short screens the two sliders collapse into a
                     * horizontal layout to cut vertical footprint roughly in half.
                     */}
                        <div className="
                        px-3 sm:px-5
                        py-2 sm:py-3
                        bg-white dark:bg-gray-800
                        border-b border-gray-100 dark:border-gray-700/50
                        shrink-0
                    ">
                            {/* Tall screens: stacked */}
                            <div className="hidden [@media(min-height:480px)]:flex flex-col gap-2.5 sm:gap-3">
                                <SliderRow
                                    left={<ZoomOut className="w-4 h-4 text-gray-400" />}
                                    right={<ZoomIn className="w-4 h-4 text-gray-400" />}
                                    value={zoom} min={1} max={3} step={0.1}
                                    onChange={setZoom}
                                    label="Zoom"
                                />
                                <SliderRow
                                    left={<RotateCw className="w-4 h-4 text-gray-400" />}
                                    right={<span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-8 text-right tabular-nums">{rotation}°</span>}
                                    value={rotation} min={0} max={360} step={1}
                                    onChange={setRotation}
                                    label="Rotation"
                                />
                            </div>

                            {/* Short screens: side by side */}
                            <div className="flex [@media(min-height:480px)]:hidden gap-3">
                                <div className="flex-1 flex items-center gap-2">
                                    <ZoomOut className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    <input
                                        type="range" value={zoom} min={1} max={3} step={0.1}
                                        aria-label="Zoom"
                                        onChange={(e) => setZoom(Number(e.target.value))}
                                        className="flex-1 h-1 rounded-lg appearance-none cursor-pointer accent-yellow-500 bg-gray-200 dark:bg-gray-700"
                                    />
                                    <ZoomIn className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                </div>
                                <div className="flex-1 flex items-center gap-2">
                                    <RotateCw className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    <input
                                        type="range" value={rotation} min={0} max={360} step={1}
                                        aria-label="Rotation"
                                        onChange={(e) => setRotation(Number(e.target.value))}
                                        className="flex-1 h-1 rounded-lg appearance-none cursor-pointer accent-yellow-500 bg-gray-200 dark:bg-gray-700"
                                    />
                                    <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 w-6 text-right tabular-nums shrink-0">
                                        {rotation}°
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* ── Footer ─────────────────────────────────────────── */}
                        <div className="
                        px-3 sm:px-5
                        py-2 sm:py-3
                        bg-white dark:bg-gray-800
                        shrink-0
                    ">
                            <div className="flex gap-2 sm:gap-3">
                                <button
                                    onClick={onCancel}
                                    className="
                                    flex-1 px-3 sm:px-5
                                    py-2 sm:py-2.5
                                    border-2 border-gray-200 dark:border-gray-700
                                    text-gray-700 dark:text-gray-300
                                    font-semibold rounded-xl
                                    hover:bg-gray-50 dark:hover:bg-gray-700
                                    transition-all text-sm
                                "
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleCrop}
                                    className="
                                    flex-1 px-3 sm:px-5
                                    py-2 sm:py-2.5
                                    bg-gradient-to-r from-yellow-500 to-yellow-600
                                    hover:from-yellow-600 hover:to-yellow-700
                                    text-white font-semibold rounded-xl
                                    transition-all shadow-md
                                    flex items-center justify-center gap-1.5
                                    text-sm
                                "
                                >
                                    <Check className="w-4 h-4 shrink-0" />
                                    Appliquer
                                </button>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>

    );
};

/* ── Shared slider row (used in tall layout) ─────────────────────────── */
const SliderRow = ({ left, right, value, min, max, step, onChange, label }) => (
    <div className="flex items-center gap-3">
        <span className="shrink-0">{left}</span>
        <input
            type="range"
            value={value} min={min} max={max} step={step}
            aria-label={label}
            onChange={(e) => onChange(Number(e.target.value))}
            className="flex-1 h-1.5 rounded-lg appearance-none cursor-pointer accent-yellow-500 bg-gray-200 dark:bg-gray-700"
        />
        <span className="shrink-0">{right}</span>
    </div>
);

export default ImageCropperModal;